import Page from '../Page';
import LoadingIndicator from '../components/LoadingIndicator';
import RankingRulesCard from '../components/RankingRulesCard';
import {useEffect, useMemo, useRef, useState} from 'preact/hooks';
import {useLocation} from 'preact-iso';
import {loadCollectionSort, applyCollectionSort, setAutoSort, SORT_ORDER_LABELS} from '../../../../shared/models/collection-sort';
import {SALES_WINDOW_DAYS} from '../../../../shared/collection-sort.js';

const PAGE_SIZE = 100;
// Off until the nightly job that re-applies the sort exists; the setting would otherwise do nothing.
const AUTO_SORT_AVAILABLE = false;
const PUBLISHED_FORMAT = new Intl.DateTimeFormat(undefined, {dateStyle: 'medium'});
const message = (error, fallback) => error instanceof Error ? error.message : fallback;

// One quiet line in ranking order; only out of stock, the rule that demotes, stands out.
function Criteria({criteria, status}) {
  const parts = [
    // Draft and archived products stay in the collection but are hidden on the storefront.
    status !== 'ACTIVE' && {text: status === 'ARCHIVED' ? 'Archived' : 'Draft'},
    criteria.inStock ? {text: 'In stock'} : {text: 'Out of stock', tone: 'critical'},
    criteria.isNew && {text: 'New'},
    {text: criteria.isEvp ? 'EVP' : 'Third-party'},
    // Without sales data the ranking falls back to Shopify's best-selling position.
    {text: criteria.unitsSold == null ? `Best seller #${criteria.salesRank}` : `${criteria.unitsSold} sold`},
    // The date only ranks when sales data loaded.
    criteria.unitsSold != null && {text: criteria.publishedAt == null ? 'Not published' : `Published ${PUBLISHED_FORMAT.format(criteria.publishedAt)}`},
  ].filter(Boolean);
  return <s-paragraph color="subdued">
    {parts.map((part, index) => <>{index > 0 && <s-text color="subdued" fontSize="small-200"> · </s-text>}<s-text color="subdued" fontSize="small-200" tone={part.tone}>{part.text}</s-text></>)}
  </s-paragraph>;
}

function Movement({from, to}) {
  if (from === to) return <s-text color="subdued">–</s-text>;
  const up = from > to;
  return <s-stack direction="inline" gap="small-500" alignItems="center">
    <s-icon type={up ? 'arrow-up' : 'arrow-down'} tone={up ? 'success' : 'critical'} size="small" />
    <s-text tone={up ? 'success' : 'critical'}>{Math.abs(from - to)}</s-text>
  </s-stack>;
}

function OrderTable({entries, showCriteria, currentIndex}) {
  return <s-table>
    <s-table-header-row>
      <s-table-header listSlot="kicker">#</s-table-header>
      <s-table-header listSlot="primary">Product</s-table-header>
      {currentIndex && <s-table-header listSlot="labeled">Change</s-table-header>}
    </s-table-header-row>
    <s-table-body>{entries.map(({product, criteria}, index) => (
      <s-table-row key={product.id}>
        <s-table-cell>{index + 1}</s-table-cell>
        <s-table-cell>
          <s-grid gridTemplateColumns="auto 1fr" gap="small" alignItems="start">
            <s-thumbnail src={product.featuredMedia?.preview?.image?.url} alt="" size="small-200" />
            <s-stack gap="small-200">
              <s-link tone="neutral" href={`shopify:admin/products/${product.id.split('/').pop()}`} target="_blank"><s-text>{product.title}</s-text></s-link>
              {showCriteria && <Criteria criteria={criteria} status={product.status} />}
            </s-stack>
          </s-grid>
        </s-table-cell>
        {currentIndex && <s-table-cell><Movement from={currentIndex.get(product.id)} to={index} /></s-table-cell>}
      </s-table-row>
    ))}</s-table-body>
  </s-table>;
}

export default function CollectionSortPage({id}) {
  const location = useLocation();
  const collectionId = `gid://shopify/Collection/${id}`;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [progress, setProgress] = useState('');
  const [showCriteria, setShowCriteria] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const autoSortModal = useRef(null);

  const load = () => {
    setStatus('loading'); setError('');
    return loadCollectionSort(collectionId).then(setData)
      .catch((e) => setError(message(e, 'Unable to load this collection.')))
      .finally(() => setStatus('idle'));
  };
  useEffect(() => { load(); }, [collectionId]);

  // Draft and archived products are hidden from shoppers, so by default positions are counted
  // the way the storefront shows them. Apply still orders every product in the collection.
  const {current, proposed, hidden} = useMemo(() => {
    const shown = ({product}) => showInactive || product.status === 'ACTIVE';
    const all = data?.current || [];
    const current = all.filter(shown);
    return {current, proposed: (data?.proposed || []).filter(shown), hidden: all.length - current.length};
  }, [data, showInactive]);
  const currentIndex = useMemo(() => new Map(current.map(({product}, index) => [product.id, index])), [current]);
  const changed = useMemo(() => {
    const index = new Map((data?.current || []).map(({product}, position) => [product.id, position]));
    return (data?.proposed || []).filter(({product}, position) => index.get(product.id) !== position).length;
  }, [data]);
  const busy = status !== 'idle';

  const apply = async () => {
    setStatus('applying'); setError(''); setNotice('');
    try {
      const {moved} = await applyCollectionSort({
        id: collectionId, sortOrder: data.collection.sortOrder,
        expectedIds: data.current.map(({product}) => product.id),
        targetIds: data.proposed.map(({product}) => product.id),
        onProgress: setProgress,
      });
      setNotice(moved ? `Sort applied. ${moved} ${moved === 1 ? 'product' : 'products'} moved.` : 'Sort applied. No products needed to move.');
      shopify.toast.show('Sort applied');
      await load();
    } catch (e) {
      setError(message(e, 'Unable to apply the sort.'));
      setStatus('idle');
    } finally { setProgress(''); }
  };

  // Turning on can overwrite a hand-set order, so it waits for confirmation; turning off saves at once.
  const toggleAutoSort = (event) => {
    if (!event.currentTarget.checked) return updateAutoSort(false);
    event.currentTarget.checked = false;
    autoSortModal.current?.showOverlay();
  };

  const updateAutoSort = async (enabled) => {
    setStatus('saving'); setError('');
    try {
      await setAutoSort(collectionId, enabled);
      setData((previous) => ({...previous, collection: {...previous.collection, autoSort: enabled}}));
      shopify.toast.show(enabled ? 'Automatic sorting on' : 'Automatic sorting off');
    } catch (e) { setError(message(e, 'Unable to update automatic sorting.')); }
    finally { setStatus('idle'); }
  };

  const collection = data?.collection;
  return (
    <Page heading={collection?.title || 'Collection sort'}>
      <s-link slot="breadcrumb-actions" href="/collections" onClick={(event) => { event.preventDefault(); location.route('/collections'); }}>Collections</s-link>
      {error && <s-banner tone="critical">{error}</s-banner>}
      {notice && <s-banner tone="success" dismissible onDismiss={() => setNotice('')}>{notice}</s-banner>}
      {data?.salesError && <s-banner tone="warning" heading="Sales data unavailable">Ranking by Shopify's best-selling order instead of units sold. {data.salesError}</s-banner>}
      {status === 'applying' && <s-banner tone="info">{progress || 'Applying sort…'} Keep this page open until it finishes.</s-banner>}
      {!data ? (status === 'loading' ? <s-section><LoadingIndicator label="Loading collection" /></s-section> : null) : <>
        <s-section heading="Settings">
          <s-query-container>
            <s-grid gridTemplateColumns="@container (inline-size > 640px) 1fr 1fr, 1fr" gap="large">
              <s-stack gap="base">
                <s-switch label="Show ranking criteria" details={`Listed in ranking order: stock, new, brand, then units sold in the last ${SALES_WINDOW_DAYS} days, then newest published.`} checked={showCriteria} onChange={(event) => setShowCriteria(event.currentTarget.checked)} />
                <s-switch label="Show draft and archived products" details={hidden ? `${hidden} ${hidden === 1 ? 'product is' : 'products are'} hidden from shoppers and from this list.` : 'Shoppers don’t see draft or archived products.'} checked={showInactive} onChange={(event) => setShowInactive(event.currentTarget.checked)} />
              </s-stack>
              <s-stack gap="base">
                <s-switch label="Automatic sorting" checked={collection.autoSort} disabled={busy || !AUTO_SORT_AVAILABLE} onChange={toggleAutoSort}
                  details={!AUTO_SORT_AVAILABLE
                    ? 'Coming soon. This sort will be re-applied nightly and the collection kept on manual sorting.'
                    : collection.autoSort
                      ? 'This sort is re-applied nightly. Any order set by hand in Shopify admin is replaced at the next run.'
                      : 'When on, this sort is re-applied nightly and the collection stays on manual sorting.'} />
              </s-stack>
            </s-grid>
          </s-query-container>
        </s-section>
        <s-section>
          <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
            <s-stack gap="small-500">
              <s-text><s-text fontWeight="semibold">{data.proposed.length} {data.proposed.length === 1 ? 'product' : 'products'}</s-text> · {changed ? `${changed} ${changed === 1 ? 'position changes' : 'positions change'}` : 'No changes'}</s-text>
              <s-text color="subdued">Applying a sort changes the collection to manual sorting.</s-text>
            </s-stack>
            <s-stack direction="inline" gap="small">
              <s-button onClick={load} loading={status === 'loading'} disabled={busy}>Refresh</s-button>
              <s-button variant="primary" commandFor="apply-sort-modal" command="--show" loading={status === 'applying'} disabled={busy || !changed}>Apply custom sort</s-button>
            </s-stack>
          </s-grid>
        </s-section>
        <s-query-container>
          <s-grid gridTemplateColumns="@container (inline-size > 760px) 1fr 1fr, 1fr" gap="base">
            <s-section heading={`Current sort (${SORT_ORDER_LABELS[collection.sortOrder] || collection.sortOrder})`} padding="none">
              <OrderTable entries={current.slice(0, visible)} showCriteria={showCriteria} />
            </s-section>
            <s-section heading="Custom sort" padding="none">
              <OrderTable entries={proposed.slice(0, visible)} showCriteria={showCriteria} currentIndex={currentIndex} />
            </s-section>
          </s-grid>
        </s-query-container>
        {visible < proposed.length && <s-stack alignItems="center" padding="base">
          <s-button onClick={() => setVisible(visible + PAGE_SIZE)}>Show {Math.min(PAGE_SIZE, proposed.length - visible)} more</s-button>
        </s-stack>}
        {/* The list grid has no bottom margin of its own, unlike top-level sections. */}
        <s-box paddingBlockStart="base"><RankingRulesCard /></s-box>
      </>}
      <s-modal id="apply-sort-modal" heading="Apply custom sort?">
        <s-paragraph>The collection will switch to manual sorting and its products will be reordered to match the proposed order.</s-paragraph>
        <s-button slot="secondary-actions" commandFor="apply-sort-modal" command="--hide">Cancel</s-button>
        <s-button slot="primary-action" variant="primary" commandFor="apply-sort-modal" command="--hide" onClick={apply}>Apply custom sort</s-button>
      </s-modal>
      <s-modal ref={autoSortModal} id="auto-sort-modal" heading="Turn on automatic sorting?">
        <s-stack gap="base">
          <s-paragraph>Every night, this collection will be switched to manual sorting if needed and reordered to match this sort.</s-paragraph>
          <s-paragraph>Any order set by hand in Shopify admin, and the collection's current sort setting, will be replaced. This can't be undone automatically.</s-paragraph>
        </s-stack>
        <s-button slot="secondary-actions" commandFor="auto-sort-modal" command="--hide">Cancel</s-button>
        <s-button slot="primary-action" variant="primary" tone="critical" commandFor="auto-sort-modal" command="--hide" onClick={() => updateAutoSort(true)}>Turn on</s-button>
      </s-modal>
    </Page>
  );
}
